import { ShopperAgentGenerationMode } from "@redotech/cockroachdb-codegen/tables/shopper-ai/shopper-agent-generation-mode";
import { ShopperAgentOutboundMessageKind } from "@redotech/cockroachdb-codegen/tables/shopper-ai/shopper-agent-outbound-message-kind";
import { zodToEffectSchema } from "@redotech/effect-api-shared-common/zod";
import { productFamilyIdFormat } from "@redotech/redo-product-id";
import { ShopperAgentAutomationKind } from "@redotech/redo-shopper-agent-common/automation-kind";
import { ShopperAgentOutreachOrigin } from "@redotech/redo-shopper-agent-common/outreach-origin";
import { Schema } from "effect";
import {
  ChannelThreadId,
  MessageId,
  SupportTicketId,
  TopicId,
} from "../../../ids/definitions";
import { enumLiterals } from "../../../schema/helpers";
import { CustomerBasicInfo } from "../../customer";
import { Message, MessageChannel } from "./message";
import { RelatedShopperAgentAggregateInsight } from "./shopper-agent-aggregate-insight";
import {
  SupportTicketAssigneeType,
  SupportTicketStatus,
} from "./support-ticket";

/** Only non-closed handoff targets are surfaced here. */
export const ShopperAgentEscalatedSupportTicket = Schema.Struct({
  id: SupportTicketId,
  status: SupportTicketStatus,
});
export type ShopperAgentEscalatedSupportTicket =
  typeof ShopperAgentEscalatedSupportTicket.Type;

/** Shopper agent conversations list row (topic + lane counts/timestamps). */
export const ShopperAgentConversationListItem = Schema.Struct({
  topicId: TopicId,
  channelThreadId: ChannelThreadId,
  channel: MessageChannel,
  customer: Schema.NullOr(CustomerBasicInfo),
  numCustomerMessages: Schema.Number,
  numMerchantMessages: Schema.Number,
  lastCustomerMessageAt: Schema.NullOr(Schema.Date),
  lastMerchantMessageAt: Schema.NullOr(Schema.Date),
  lastCustomerMessageContent: Schema.NullOr(Schema.String),
  escalatedSupportTicket: Schema.NullOr(ShopperAgentEscalatedSupportTicket),
  createdAt: Schema.Date,
  updatedAt: Schema.Date,
});
export type ShopperAgentConversationListItem =
  typeof ShopperAgentConversationListItem.Type;

export const ShopperAgentConversationListResponse = Schema.Struct({
  items: Schema.Array(ShopperAgentConversationListItem),
  hasMore: Schema.Boolean,
});
export type ShopperAgentConversationListResponse =
  typeof ShopperAgentConversationListResponse.Type;

export const ShopperAgentConversationFilterTab = Schema.Literal(
  "all",
  "awaiting",
  "replied",
  "escalated",
);
export type ShopperAgentConversationFilterTab =
  typeof ShopperAgentConversationFilterTab.Type;

/**
 * Why one message exists: how it was produced, and which outreach it belongs
 * to. Only outbound shopper-agent messages have a row.
 */
export const ShopperAgentMessageProvenance = Schema.Struct({
  messageId: MessageId,
  /** Groups messages into outreach sections; null when the link is missing. */
  outreachTopicId: Schema.NullOr(TopicId),
  outboundMessageKind: Schema.Enums(ShopperAgentOutboundMessageKind),
  primaryTriggerKey: Schema.NullOr(Schema.String),
  generationMode: Schema.NullOr(Schema.Enums(ShopperAgentGenerationMode)),
  sentViaHumanQueue: Schema.Boolean,
  outreachOrigin: Schema.NullOr(
    Schema.Literal(...enumLiterals(ShopperAgentOutreachOrigin)),
  ),
  outreachAutomationKind: Schema.NullOr(
    Schema.Literal(...enumLiterals(ShopperAgentAutomationKind)),
  ),
  outreachCampaignId: Schema.NullOr(Schema.String),
  outreachAutomationId: Schema.NullOr(Schema.String),
  outreachAutomationStepId: Schema.NullOr(Schema.String),
});
export type ShopperAgentMessageProvenance =
  typeof ShopperAgentMessageProvenance.Type;

export const ShopperAgentConversationAnalysisSentiment = Schema.Literal(
  "positive",
  "neutral",
  "negative",
  "mixed",
);
export type ShopperAgentConversationAnalysisSentiment =
  typeof ShopperAgentConversationAnalysisSentiment.Type;

export const ShopperAgentProductFamilyId = zodToEffectSchema(
  productFamilyIdFormat.schema,
);

/** Latest displayable analysis window for one conversation. */
export const ShopperAgentConversationAnalysis = Schema.Struct({
  summary: Schema.String,
  sentiment: Schema.NullOr(ShopperAgentConversationAnalysisSentiment),
  topics: Schema.Array(Schema.String),
  analyzedAt: Schema.Date,
});
export type ShopperAgentConversationAnalysis =
  typeof ShopperAgentConversationAnalysis.Type;

/** Detail payload; messages are the shared Message union. */
export const ShopperAgentConversationDetailResponse = Schema.Struct({
  topicId: TopicId,
  channelThreadId: ChannelThreadId,
  channel: MessageChannel,
  customer: Schema.NullOr(CustomerBasicInfo),
  messages: Schema.Array(Message),
  supportTicketId: Schema.NullOr(SupportTicketId),
  assigneeType: Schema.NullOr(SupportTicketAssigneeType),
  supportTicketStatus: Schema.NullOr(SupportTicketStatus),
  analysis: Schema.NullOr(ShopperAgentConversationAnalysis),
  productFamilyIds: Schema.Array(ShopperAgentProductFamilyId),
  /** First message of the latest matching outreach when an outreachTopic filter is active. */
  scrollToMessageId: Schema.optional(Schema.NullOr(MessageId)),
  /** Sparse — only outbound shopper-agent messages appear. */
  messageProvenance: Schema.Array(ShopperAgentMessageProvenance),
  relatedAggregateInsights: Schema.Array(RelatedShopperAgentAggregateInsight),
  smsOptedOutAt: Schema.NullOr(Schema.Date),
  /**
   * True when a drafted reply for this thread is waiting in the human review
   * queue. Absent on older responses.
   */
  pendingHumanReviewReply: Schema.optional(Schema.Boolean),
});
export type ShopperAgentConversationDetailResponse =
  typeof ShopperAgentConversationDetailResponse.Type;
